import React, { useMemo, useState } from 'react';
import {
  getAdminPaymentStatistics,
  getFilteredPayments,
  getPaymentLink,
  InitializeHubtelDirectCharge,
  sendPayNowNotification
} from '../../../api/requests';
import { formatCurrencyAmount, formatPercentageValue, getPhoneNumberFromShortCodeFormat, showToast } from '../../../utils/miscFunctions';
import { ITableAction } from '../../../components/elements/GTable/GTable';
import GTable from '../../../components/elements/GTable';
import Page from '../../../components/PageWrapper';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useUserAccount from '../../../hooks/userAccountHook';
import useRoute from '../../../hooks/routesHook';
import { PAYMENTS_FILTER_OPTIONS, PRODUCT_TYPES } from '../../../resources/enums';
import moment from 'moment';
import { IGeneralMetrics } from '../../../models/generalMetrics/IGeneralMetrics';
import PaymentStatus from '../../../components/PaymentStatus';
import PaymentsOverdueTabs from '../../../components/PaymentsOverdueTabs';
import useModal from '../../../hooks/modalHook';
import PaymentLinkModal from '../policies/auto/paymentLinkModal';
import PopOnMe from '../../../components/elements/poponme/PopOnMe';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import { ICustomerPayments, ITableFilterItem } from '../../../resources/interfaces';
import useSSE from '../../../hooks/useSSE';
import ApproveModal from './PaymentApproval';
import { useQuery } from '@tanstack/react-query';
import useLayoutHook from '../../../hooks/layoutHook';
import TableFilter from '../../../components/elements/GTable/InGTableComponents/TableFilter';
import TableFilterForm from '../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../states/slices/resourceSlice';
import { AlertCircle, CalendarDays, CheckCircle2, CreditCard, DollarSign, Paperclip, Send } from 'lucide-react';
import useProduct from '../../../hooks/productHook';
import { userInfo } from 'os';
import { FaMoneyBill } from 'react-icons/fa6';
import { formatPhoneNumber } from 'react-phone-number-input';

function Payments(): JSX.Element {
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { user } = useUserAccount();
  const { screenWidth } = useLayoutHook();
  const { getDashboardBase } = useRoute();
  const navigate = useNavigate();
  const { ProductBadge } = useProduct();
  const [tableFilters, setTableFilters] = useState<ITableFilterItem[]>(getDefaultFilters());
  const [searchString, setSearchString] = useState<string>();
  const country = useSelector(getSelectedCountry) ?? { id: 1 };
  const [filterPaymentsBy, setFilterPaymentsBy] = useState<PAYMENTS_FILTER_OPTIONS>(!user.isBroker() ? PAYMENTS_FILTER_OPTIONS.DUE_TODAY : PAYMENTS_FILTER_OPTIONS.OUTSTANDING);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [generalMetrics, setGeneralMetrics] = useState<Partial<IGeneralMetrics>>({
    payment_amount_upcoming_this_week: 0,
    total_outstanding_payments: 0,
    all_overdue_payment_amount: 0,
    payment_amount_due_this_month: 0,
    payment_amount_success: 0,
    payment_amount_due_this_week: 0,
    payment_amount_due_today: 0,
    payment_amount_long_overdue: 0,
    payment_amount_due_for_termination: 0,
    start_due_for_termination: 0,
    start_due_later_overdue: 0,
    start_due_this_month: 0,
    start_due_this_week: 0,
    start_due_today: 0,
    start_missed: 0,
    start_outstanding: 0,
    start_overdue: 0,
    start_success: 0,
    start_total_due: 0,
    success: 0,
    week_of_year: 0,
    year: new Date().getFullYear(),
    at_risk: 0,
    collected: 0,
    collection_rate: 0,
    expected: 0
  });


  function getDefaultFilters() {
    const filters: ITableFilterItem[] = [
      {
        name: 'date_range',
        value: '',
        type: 'dateRange',
        placeholder: 'Select date',
        label: 'Date Range'
      }
    ];

    return filters;
  }

  const dateFrom = tableFilters.find((filter: ITableFilterItem) => filter.name == 'date_range')?.value?.from;
  const dateTo = tableFilters.find((filter: ITableFilterItem) => filter.name == 'date_range')?.value?.to;

  // const { data: paymentMetrics } = useSSE<IGeneralMetrics>({
  //   endpoint: `/sse/admin/metrics/payments/${country?.id}`,
  //   eventType: 'payments',
  //   onData(data) {
  //     setGeneralMetrics(data);
  //     setMetricsLoading(false)
  //   }
  // });

  const fetchFilteredPayments = async () => {
    try {
      const response = await getFilteredPayments(filterPaymentsBy, country?.id ?? 1, searchString, dateFrom, dateTo);
    
      return response;
    } catch (error: any) {
      showToast.error(error?.description ?? 'Could not load the payments. Please try again.');
    } finally {
    }
  };

  useQuery({
    queryKey: ['fetchFilteredPayments', filterPaymentsBy, searchString, dateFrom, dateTo, country],
    queryFn: fetchFilteredPayments
  });

     const {data, isLoading} = useQuery({ 
        queryKey: ['fetchFilteredPayments', filterPaymentsBy, searchString, dateFrom, dateTo, country],
        queryFn: fetchFilteredPayments,
        staleTime: 1000 * 10,
        placeholderData: (prev) => prev,
          gcTime: 1000 * 60 * 30 * 5,
        select: (response) => {
          return {
            payments: response?.data?.data,
          };
        },
      
      });
  
      const filteredPayments = data?.payments;

  const { isLoading: metricsLoading } = useQuery({
    queryKey: ['getAdminPaymentStatistics', country],
    queryFn: async () => {
      const response: any = await getAdminPaymentStatistics(country?.id ?? 1)
      if (response) {
        setGeneralMetrics(response);
      }
    }
  });

  const paymentsColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Customer',
            accessor: 'customerName',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Contact',
            accessor: 'customerContact',
            Cell: (row: any) => {
              return <span>{getPhoneNumberFromShortCodeFormat(row.cell.value) ?? '-'}</span>;
            }
          },
          {
            Header: 'Policy ID',
            accessor: 'policyNumber',
            Cell: (row: any) => {
              return (
                <Link
                  to={row?.row?.original?.productType?.key === 'PREMIUM_FINANCING'
                    ? `${getDashboardBase()}/premium-financing/${row.cell.value}`
                    : `${getDashboardBase()}/policies/${row.cell.value ?? row?.row?.original.ref}?type=motor`}>
                  <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">
                    {row.cell.value ?? row?.row?.original.ref ?? '-'}
                  </span>
                </Link>
              );
            }
          },
          {
            Header: 'Product Type',
            accessor: 'productType.key',
            Cell: (row: any) => {

              return <ProductBadge productType={row.cell.value} />
            }
          },

          {
            Header: 'Inception Date',
            accessor: 'inceptionDate',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YY') : '-'}</span>
              );
            }
          },
          {
            Header: 'Intermediary',
            accessor: 'broker',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Currency',
            accessor: 'currency',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },

          {
            Header: 'Scheduled Amount',
            accessor: 'scheduledAmount',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Penalty',
            accessor: 'penalty',
            Cell: (row: any) => {
              return <span className="">{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Total',
            accessor: 'amount',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Due Date',
            accessor: 'dueDate',
            Cell: (row: any) => {
              return (
                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YY') : '-'}</span>
              );
            }
          },
          {
            Header: `${filterPaymentsBy === PAYMENTS_FILTER_OPTIONS.OVERDUE ? 'Days Overdue' : 'Payment Due'
              }`,
            accessor: '',
            Cell: (row: any) => {
              return (
                <span>
                  {row.row.original ? moment(row.row.original?.dueDate).fromNow() : '-'}
                </span>
              );
            }
          },
          {
            Header: 'Status',
            accessor: 'paymentStatus',
            Cell: (row: any) => {
              return <PaymentStatus row={row} actions={getActions(row.row.original)} />;
            }
          }
        ]
      }
    ],
    []
  );

  const paymentsColumnsMobile = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Customer',
            accessor: 'customerName',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Contact',
            accessor: 'customerContact',
            Cell: (row: any) => {
              return <span>{getPhoneNumberFromShortCodeFormat(row.cell.value) ?? '-'}</span>;
            }
          },
          {
            Header: 'Product Type',
            accessor: 'productType.key',
            Cell: (row: any) => {
              return <ProductBadge productType={row.cell.value} />
            }
          },

          {
            Header: 'Amount',
            accessor: 'amount',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            id: 'Action',
            Header: 'Actions',
            Cell: (row: any) => {
              if (getActions(row.row.original)?.length > 0)
                return (
                  <div className="cursor-pointer flex justify-center">
                    <PopOnMe actions={getActions(row.row.original)}>
                      <EllipsisVerticalIcon className="h-4 w-4" />
                    </PopOnMe>
                  </div>
                );
            }
          }
        ]
      }
    ],
    []
  );

  const showPaymentLinkModal = (paymentLink: string) => {
    updateModalStates({
      show: true,
      size: 'lg',
      children: (
        <PaymentLinkModal
          onProceed={() => {
            closeModal();
          }}
          onClose={closeModal}
          link={paymentLink}
        />
      ),

      title: 'Payment Link',
      classes: '!p-0 r-[5px]'
    });
  };

  const generatePaymentLink = (paymentId: string) => {
    getPaymentLink(paymentId)
      .then((response) => {
        const paymentLink = response?.data?.data;
        showPaymentLinkModal(paymentLink);
      })
      .catch((error: any) => {
        showToast.error(error?.message ?? 'Failed to generate payment link. Please try again later.');
      });
  };

  const sendPaymentNotification = (payment: ICustomerPayments) => {
    if (payment) {
      sendPayNowNotification(payment?.id, payment?.productType?.key)
        .then(() => {
          showToast.success('Payment link sent successfully.');
        })
        .catch(() => {
          showToast.error('Could not send the payment link. Please try again.');
        });
    } else {
      showToast.error('No payment selected to notify. Please pick a payment and try again.');
    }
  };

  const InitiateHubtelDirectCharge = (payment: ICustomerPayments) => {
    if (payment) {
      InitializeHubtelDirectCharge(payment?.id)
        .then((response: any) => {
          showToast.success(response?.message ?? 'Wallet charge started successfully.');
        })
        .catch((error: any) => {
          showToast.error(error?.description ?? 'Could not start the wallet charge. Please try again.');
        });
    } else {
      showToast.error('No payment selected to charge. Please pick a payment and try again.');
    }
  };

  const getActions = (payment: any): ITableAction[] => {
    const actions = [];

    if (payment && payment?.paymentStatus !== 'SUCCESS' && payment?.paymentStatus !== 'VOID') {
      actions.push(
        {
          label: 'Get payment link',
          icon: <Paperclip className='h-4 w-4' />,
          callback: () => {
            if (payment) generatePaymentLink(payment.id);
          }
        },
        {
          label: 'Send payment notification',
          icon: <Send className='h-4 w-4' />,
          callback: () => {
            if (payment) sendPaymentNotification(payment);
          }
        },
        // Initiate direct charge hidden — wallet-debit flow not used in
        // this release; uncomment when broker→wallet path is re-enabled.
        // {
        //   label: 'Initiate direct charge',
        //   icon: <CreditCard className='h-4 w-4' />,
        //   callback: () => {
        //     if (payment) {
        //       InitiateHubtelDirectCharge(payment);
        //     }
        //   }
        // }
      );

    }
    return actions;
  };

  return (
    <Page title="Payments">
      {/* Payments */}
      <div className="bg-white dark:bg-darkBg min-h-full w-full p-5  rounded-lg shadow-md">
        {!user.isInsurer() && (
          <div className="rounded-md  mb-1 justify-between px-2 pb-3  align-items-center">
            <div className="">
              <div className="inline-flex items-center gap-2 mb-2">
                <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                <h2 className="font-semibold text-3xl text-gray-900 dark:text-darkBg-mainText">Payments Metrics</h2>
              </div>

              <p className="text-sm text-slate-400 dark:text-darkBg-subText">
                View a list of all payments and associated details.
              </p>
            </div>

            {metricsLoading && <PaymentMetricsLoader />}
            {!metricsLoading &&
              <>
                {/* Week Info Header */}
                < div className="flex items-center gap-2 text-sm text-gray-500 dark:text-darkBg-mainText my-5">
                  <CalendarDays className="w-4 h-4" />
                  <span>Week {generalMetrics.week_of_year}, {generalMetrics.year}</span>
                  <span className="text-gray-300 dark:text-darkBg-subText">|</span>
                  <span className="text-xs dark:text-darkBg-subText">Tags show start of week values</span>
                </div>

                {<PaymentsOverdueTabs
                  data={{
                    at_risk: generalMetrics?.at_risk ?? 0,
                    collected: generalMetrics?.collected ?? 0,
                    collection_rate: generalMetrics?.collection_rate ?? 0,
                    expected: generalMetrics?.expected ?? 0,
                    outstanding: generalMetrics?.outstanding ?? 0,
                    missed: generalMetrics?.missed ?? 0,
                    missed_due_today: generalMetrics?.missed_due_today ?? 0,
                    overdue: generalMetrics?.overdue ?? 0,
                    due_today: generalMetrics?.due_today ?? 0,
                    due_this_week: generalMetrics?.due_this_week ?? 0,
                    due_this_month: generalMetrics?.due_this_month ?? 0,
                    long_overdue: generalMetrics?.long_overdue ?? 0,
                    success: generalMetrics?.success ?? 0,
                    due_later: generalMetrics?.due_later ?? 0,
                    due_for_termination: generalMetrics?.due_for_termination ?? 0,
                    start_due_for_termination: 0,
                    start_due_later_overdue: 0,
                    start_due_this_month: 0,
                    start_due_this_week: 0,
                    start_due_today: 0,
                    start_missed: 0,
                    start_outstanding: 0,
                    start_overdue: 0,
                    start_success: 0,
                    start_total_due: 0,
                    week_of_year: 0,
                    year: new Date().getFullYear()
                  }}
                  onFilterChange={(filterBy: PAYMENTS_FILTER_OPTIONS) => {
                    setFilterPaymentsBy(filterBy);
                  }}
                  currentFilter={filterPaymentsBy}
                  metricsLoading={metricsLoading}
                />
                }

                {/* Summary Stats Bar */}
                {user?.isInternalAdmin() && (
                  <div className="flex flex-wrap items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Collection Rate</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {generalMetrics ? formatPercentageValue(generalMetrics.collection_rate, 2, true) : 0}
                        </p>
                      </div>
                    </div>
                    <div className="w-px h-10 bg-gray-200 dark:bg-gray-600 hidden sm:block" />
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">At Risk</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {generalMetrics ? formatCurrencyAmount(generalMetrics.at_risk) : '0'}
                        </p>
                      </div>
                    </div>
                    <div className="w-px h-10 bg-gray-200 dark:bg-gray-600 hidden sm:block" />
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <CalendarDays className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Expected This Week</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {generalMetrics ? formatCurrencyAmount(generalMetrics.expected) : '0'}
                        </p>
                      </div>
                    </div>
                    <div className="w-px h-10 bg-gray-200 dark:bg-gray-600 hidden sm:block" />
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <FaMoneyBill className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Collected</p>
                        <p className="text-lg font-bold text-green-600 dark:text-green-400">
                          {generalMetrics ? formatCurrencyAmount(generalMetrics.collected) : '0'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            }
            <div className="">
              <GTable
                classes="h-fit "
                columns={screenWidth <= 550 ? paymentsColumnsMobile : paymentsColumns}
                data={filteredPayments || []}
                tableType="payment"
                showSearch
                loading={isLoading}
                onRowClick={(payment: any) => {
                  payment?.productType.key === 'PREMIUM_FINANCING'
                    ? navigate(`${getDashboardBase()}/premium-financing/${payment.policyNumber}`, {
                      replace: true
                    })
                    : navigate(`${getDashboardBase()}/policies/${payment.policyNumber}?type=motor`, {
                      replace: true
                    });
                }}
                selectedFilters={tableFilters}
                onRemoveFilter={(filter: ITableFilterItem, index: number) => {
                  const _tableFilters: ITableFilterItem[] = [...tableFilters];

                  let thisFilter = { ...tableFilters[index] };
                  thisFilter = { ..._tableFilters[index], value: '', value2: '' };

                  _tableFilters[index] = thisFilter;

                  setTableFilters([..._tableFilters]);
                }}
                showFilter={
                  <TableFilter
                    content={
                      <TableFilterForm
                        filters={tableFilters}
                        getValues={(filters: ITableFilterItem[]) => {
                          setTableFilters(filters);
                          setOpenFilter(false);
                        }}
                        onResetFilters={() => setTableFilters(getDefaultFilters())}
                      />
                    }
                  />
                }
                onSearch={(searchParam: string) => setSearchString(searchParam)}
              />
            </div>
          </div>
        )}

        <ModalComponent />
      </div>
    </Page >
  );
}
export default Payments;


const PaymentMetricsLoader = () => {
  const { user } = useUserAccount();
  return (
    <>
    <div className='mt-[25px] w-[315px] h-[33px] rounded-lg bg-gray-300 dark:bg-darkBg-border text-gray-300 dark:text-darkBg-subText infiniteOpacityChange'></div>
      <div className='mt-[20px]'>
        <style>
          {`

          @keyframes opacityChange {
              
              50% {
                  opacity: 0.6;
              }
          }
            .infiniteOpacityChange {
                animation: opacityChange 1.5s linear infinite;
            }
        
        `}
        </style>
        {!user.isBroker() ? 
          <div>
            <div className={`flex w-full justify-between gap-4`}>

              <div className='w-full h-[140px] rounded-xl bg-gray-300 dark:bg-darkBg-border text-gray-300 dark:text-darkBg-subText infiniteOpacityChange'></div>
              <div className='w-full h-[140px] rounded-xl bg-gray-300 dark:bg-darkBg-border text-gray-300 dark:text-darkBg-subText infiniteOpacityChange'></div>
              <div className='w-full h-[140px] rounded-xl bg-gray-300 dark:bg-darkBg-border text-gray-300 dark:text-darkBg-subText infiniteOpacityChange'></div>
              <div className='w-full h-[140px] rounded-xl bg-gray-300 dark:bg-darkBg-border text-gray-300 dark:text-darkBg-subText infiniteOpacityChange'></div>
              <div className='w-full h-[140px] rounded-xl bg-gray-300 dark:bg-darkBg-border text-gray-300 dark:text-darkBg-subText infiniteOpacityChange'></div>

            </div>
            <div className='mt-[25px] w-full h-[80px] rounded-xl bg-gray-300 dark:bg-darkBg-border text-gray-300 dark:text-darkBg-subText infiniteOpacityChange'></div>
          </div>
          :
          <div className='grid grid-cols-2 gap-6'>
            <div className='w-full h-[140px] rounded-xl bg-gray-300 dark:bg-darkBg-border text-gray-300 dark:text-darkBg-subText infiniteOpacityChange'></div>
            <div className='w-full h-[140px] rounded-xl bg-gray-300 dark:bg-darkBg-border text-gray-300 dark:text-darkBg-subText infiniteOpacityChange'></div>
          </div>
        }

      </div>
    </>
  )
}